/**
 * Value comparison + formatting for the PitchBook import diff dialog.
 *
 * The replica stores coerced PitchBook shapes that are not always plain
 * strings/numbers, and the CRM side may hold the same information in a
 * different storage shape (entered by hand, staging imports, older syncs).
 * A literal === comparison would flag contextually-equal values as conflicts.
 *
 * Cases handled (one per parser coercion kind that can reach a CRM target):
 *  - money:  backend pre-converts {"amount_m": 62.7} -> 62700000 (crm_value);
 *            here we add number-vs-numeric-string tolerance ("62700000",
 *            "62,700,000") so storage type doesn't matter.
 *  - int:    1200 vs "1,200" / "1200" -> equal (numeric tolerance).
 *  - list:   ["food products", "packaging"] vs "food products, packaging"
 *            -> equal (canonical comma-join, order-sensitive).
 *  - date:   "2001-01-31" vs "2001-01-31T00:00:00(Z)" -> equal (date part).
 *  - url:    "https://www.acme.com/" vs "acme.com" -> equal, ONLY for URL
 *            targets (website / *_url) so plain text is never over-normalized.
 *
 * NOTE: equality here drives the Status badge (advisory). The write itself is
 * still policy-based on the backend — an Overwrite on a "Match" row simply
 * rewrites the same information in PitchBook's canonical shape.
 */

export function isEmpty(v) {
    return v === null || v === undefined || v === '' ||
        (Array.isArray(v) && v.length === 0);
}

export function asFiniteNumber(v) {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v.replace(/,/g, ''));
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

/** Canonical "a, b, c" string for arrays OR comma-separated strings. */
function asListString(v) {
    const parts = Array.isArray(v)
        ? v.map((p) => String(p).trim())
        : String(v).split(',').map((p) => p.trim());
    return parts.filter(Boolean).join(', ');
}

const ISO_DATE_RE = /^(\d{4}-\d{2}-\d{2})([T ].*)?$/;

/** "2001-01-31T00:00:00Z" -> "2001-01-31"; null when not a date-like string. */
function asDatePart(v) {
    if (typeof v !== 'string') return null;
    const m = v.trim().match(ISO_DATE_RE);
    return m ? m[1] : null;
}

function isUrlTarget(target) {
    if (!target) return false;
    return target === 'website' || target.endsWith('_url');
}

/** Scheme / www. / trailing-slash insensitive form, lowercased. */
function asComparableUrl(v) {
    return String(v).trim().toLowerCase()
        .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
        .replace(/^www\./, '')
        .replace(/\/+$/, '');
}

/**
 * Contextual equality between the to-be-written PitchBook value and the
 * current CRM value. `target` is the CRM field name (drives URL handling).
 */
export function valuesEqual(a, b, target = null) {
    if (isEmpty(a) && isEmpty(b)) return true;
    if (isEmpty(a) !== isEmpty(b)) return false;

    // Numbers, regardless of storage type or thousands separators.
    const na = asFiniteNumber(a);
    const nb = asFiniteNumber(b);
    if (na !== null && nb !== null) return Math.abs(na - nb) < 1e-6;

    // Lists vs comma-joined strings (order-sensitive on purpose).
    if (Array.isArray(a) || Array.isArray(b)) {
        return asListString(a) === asListString(b);
    }

    // Date vs datetime.
    const da = asDatePart(a);
    const db = asDatePart(b);
    if (da && db) return da === db;

    // URLs — only when the target field is a URL field.
    if (isUrlTarget(target) && typeof a === 'string' && typeof b === 'string') {
        return asComparableUrl(a) === asComparableUrl(b);
    }

    if (typeof a === 'object' || typeof b === 'object') {
        return JSON.stringify(a) === JSON.stringify(b);
    }
    return String(a).trim() === String(b).trim();
}

/** Human-readable cell text. Arrays join with commas instead of JSON braces. */
export function fmtValue(v) {
    if (isEmpty(v)) return null;
    if (Array.isArray(v)) return v.map((p) => String(p)).join(', ');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
}

/**
 * Like fmtValue but renders the parser's raw money shape ({amount_m: 62.7})
 * as "$62.7M" / "$1.2B". Used by the rep-facing PitchBook tab, which shows
 * replica values directly (no crm_value conversion step).
 */
export function fmtFeedValue(v) {
    if (v && typeof v === 'object' && !Array.isArray(v) && 'amount_m' in v) {
        const m = Number(v.amount_m);
        if (Number.isFinite(m)) {
            const abs = Math.abs(m);
            const fmt = (n) => {
                const s = n.toFixed(1).replace(/\.0$/, '');
                return s;
            };
            if (abs >= 1000) return `$${fmt(m / 1000)}B`;
            return `$${fmt(m)}M`;
        }
    }
    return fmtValue(v);
}

/** "PrimaryIndustrySector" -> "Primary Industry Sector"; keeps acronyms intact. */
export function humanizeColumn(col) {
    if (!col) return '';
    return String(col)
        .replace(/_/g, ' ')
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .trim();
}
